/**
 * Window replacement state + metro pricing — addresses GSC "Crawled - not indexed"
 * pattern on /window-replacement-cost-{state} pages by adding unique state +
 * metro level pricing data that doesn't appear on any other page.
 *
 * Each entry has:
 *   - headline: state-specific one-liner used in the page intro callout
 *   - drivers: 3 window-specific cost drivers (climate, code, labor)
 *   - metros:  array of { city, low, high } typical installed cost for a SINGLE
 *              mid-tier vinyl double-hung window replacement (35-39"x 49-55").
 *              Used as a metro pricing table on the page.
 *
 * Methodology: per-window installed cost averages across the metro, mid-tier
 * energy-rated vinyl ($300-500 product cost) + standard install + disposal.
 * High end reflects upcharges for larger sizes (40-55" wide) or full-frame
 * replacement on a stucco/brick exterior. Triple-pane and fiberglass upgrades
 * NOT included — those add 35-55% on top.
 */

export const WINDOW_DRIVERS = {
  AL: {
    headline: "Alabama window replacements run 8-14% below national average — soft labor market plus year-round install season.",
    drivers: [
      { title: "Mild climate = ENERGY STAR Southern zone", body: "Alabama qualifies for ENERGY STAR Southern climate-zone certification (U-factor ≤0.40, SHGC ≤0.25). Cheaper than Northern triple-pane requirements." },
      { title: "Year-round install season", body: "No winter shutdown drives consistent installer utilization. Birmingham and Huntsville crews can run 200+ install days/year." },
      { title: "Hurricane impact ratings on Gulf Coast", body: "Mobile and Baldwin County properties need impact-rated windows (or shutters) per code. Adds $80-$220 per window." },
    ],
    metros: [
      { city: "Birmingham", low: 550, high: 920 },
      { city: "Huntsville", low: 540, high: 900 },
      { city: "Mobile (impact)", low: 700, high: 1180 },
      { city: "Montgomery", low: 520, high: 880 },
    ],
  },
  AK: {
    headline: "Alaska window replacements run 25-40% above national — shipping plus triple-pane code make this one of the most expensive states.",
    drivers: [
      { title: "Triple-pane mandate", body: "Alaska Energy Code requires U-factor ≤0.27 in most boroughs, effectively requiring triple-pane glass. Adds $250-$450 per window over double-pane." },
      { title: "Shipping premium", body: "Every window ships from Lower 48. Container freight + barge to Alaska adds $80-$220 per window before any markup." },
      { title: "Short install season", body: "May-September is the bulk of install work. Peak-season labor runs $85-$130/hr." },
    ],
    metros: [
      { city: "Anchorage", low: 920, high: 1480 },
      { city: "Fairbanks", low: 980, high: 1580 },
      { city: "Juneau (barge premium)", low: 1050, high: 1700 },
    ],
  },
  AZ: {
    headline: "Arizona window replacements run 2-6% below national — Phoenix density keeps installer pricing competitive despite UV-driven product spec requirements.",
    drivers: [
      { title: "Low-E SHGC requirement", body: "Arizona climate zone requires SHGC ≤0.25 to control solar gain. Premium Low-E coatings (Cardinal LoE-366, Solarban 70) cost $40-$80 more per window but cut cooling bills 20-30%." },
      { title: "Stucco wall full-frame install", body: "60%+ of AZ homes have stucco exteriors. Full-frame replacement (vs. insert/retrofit) requires stucco cut + patch + repaint — adds $200-$450 per opening." },
      { title: "Deep installer pool", body: "Phoenix has 200+ active window installers. Real competition keeps labor rates $45-$70/hr vs. $50-$80 national average." },
    ],
    metros: [
      { city: "Phoenix", low: 580, high: 980 },
      { city: "Tucson", low: 560, high: 940 },
      { city: "Flagstaff (cold-climate)", low: 700, high: 1180 },
      { city: "Yuma", low: 570, high: 960 },
    ],
  },
  AR: {
    headline: "Arkansas window replacements run 12-18% below national — among the cheapest states for replacement windows.",
    drivers: [
      { title: "Low installer rates", body: "Trade labor in Arkansas runs $32-$50/hr — 25-40% below the national average." },
      { title: "Mixed-humid ENERGY STAR zone", body: "Standard double-pane Low-E meets ENERGY STAR in Arkansas — no premium triple-pane spec required." },
      { title: "Tornado-prone reinforcement", body: "Some northwest Arkansas insurers offer discounts for laminated-glass impact windows ($90-$180 extra per window)." },
    ],
    metros: [
      { city: "Little Rock", low: 490, high: 820 },
      { city: "Fayetteville / Bentonville", low: 510, high: 850 },
      { city: "Fort Smith", low: 470, high: 790 },
    ],
  },
  CA: {
    headline: "California window replacements run 30-45% above national — Title 24 cool-glass requirements plus the highest labor rates in the U.S.",
    drivers: [
      { title: "Title 24 NFRC requirements", body: "California Title 24 Part 6 requires U-factor ≤0.30 and SHGC ≤0.23 in most climate zones — stricter than ENERGY STAR. Limits product choice and adds $40-$100 per window." },
      { title: "WUI fire-rated glazing", body: "Homes in WUI zones (Cal Fire FRAP map) need tempered or dual-pane fire-resistant glass on walls within 10 ft of property line. Adds $120-$280 per window." },
      { title: "California labor rates", body: "Bay Area + LA window installer hourly rates run $85-$140 vs. $50-$80 national average. Sales tax (~9.5%) applies to product cost on top." },
    ],
    metros: [
      { city: "Los Angeles", low: 850, high: 1450 },
      { city: "San Francisco Bay Area", low: 980, high: 1680 },
      { city: "Oakland / East Bay", low: 920, high: 1580 },
      { city: "San Jose / Silicon Valley", low: 950, high: 1640 },
      { city: "San Diego", low: 820, high: 1380 },
      { city: "Orange County (Irvine, Anaheim)", low: 880, high: 1500 },
      { city: "Sacramento", low: 780, high: 1320 },
      { city: "Riverside / San Bernardino (Inland Empire)", low: 760, high: 1290 },
      { city: "Fresno / Central Valley", low: 720, high: 1220 },
      { city: "Bakersfield", low: 700, high: 1180 },
      { city: "Long Beach", low: 840, high: 1430 },
    ],
  },
  CO: {
    headline: "Colorado window replacements run 8-15% above national — altitude + freeze-thaw cycles drive code, plus mountain-town premiums.",
    drivers: [
      { title: "Low U-factor requirement", body: "Colorado IECC climate zones 5-7 require U-factor ≤0.30, often pushing dual-pane with argon or triple-pane spec. Adds $80-$220 per window." },
      { title: "Freeze-thaw caulk and flashing", body: "70-90 freeze-thaw cycles per year stress window flashing and sealants. Quality installs use butyl-flange sealing instead of caulk — adds $40-$80 per window." },
      { title: "Mountain-town premium", body: "Aspen, Vail, Breckenridge, Telluride all run 30-55% above Front Range pricing due to drive time and labor shortage." },
    ],
    metros: [
      { city: "Denver metro", low: 680, high: 1140 },
      { city: "Boulder", low: 740, high: 1240 },
      { city: "Colorado Springs", low: 660, high: 1100 },
      { city: "Fort Collins", low: 670, high: 1120 },
      { city: "Aspen / Vail (mountain)", low: 980, high: 1850 },
    ],
  },
  CT: {
    headline: "Connecticut window replacements run 15-22% above national — Northeast labor rates plus stringent energy code.",
    drivers: [
      { title: "Low U-factor Northeast spec", body: "IECC Climate Zone 5/6 requires U-factor ≤0.30. Argon-filled triple-pane is common at higher tier. Adds $200-$400 per window over base spec." },
      { title: "Historic district approvals", body: "New Haven, Hartford, Stamford, and many shore towns have historic-district overlays requiring approved profiles and divided-lite patterns — adds 2-6 weeks to permitting." },
      { title: "Northeast labor premium", body: "Connecticut installer rates run $70-$110/hr vs. $50-$80 national." },
    ],
    metros: [
      { city: "Hartford", low: 720, high: 1240 },
      { city: "New Haven", low: 740, high: 1280 },
      { city: "Stamford / Fairfield County", low: 820, high: 1420 },
      { city: "Bridgeport", low: 700, high: 1200 },
    ],
  },
  DE: {
    headline: "Delaware window replacements run 4-9% above national — small-state pricing tracks closely to the Philadelphia/Baltimore corridor.",
    drivers: [
      { title: "Northeast IECC spec", body: "Delaware adopts IECC with U-factor ≤0.32 — slightly less strict than CT/NY but stricter than Southern states." },
      { title: "Coastal salt-air corrosion", body: "Rehoboth, Bethany, and Dewey Beach properties need stainless or upgraded weatherstripping hardware to resist salt-air corrosion — adds $30-$70 per window." },
      { title: "Limited installer pool", body: "Small state with thinner installer market than PA or MD — bid spread tends to be narrower." },
    ],
    metros: [
      { city: "Wilmington", low: 640, high: 1080 },
      { city: "Dover", low: 600, high: 1020 },
      { city: "Rehoboth / coastal", low: 700, high: 1180 },
    ],
  },
  FL: {
    headline: "Florida window replacements run 18-30% above national — hurricane impact ratings are mandatory in most coastal counties.",
    drivers: [
      { title: "HVHZ impact-rated glass", body: "Miami-Dade + Broward (High Velocity Hurricane Zone) require Miami-Dade NOA-rated impact windows. Cost: $850-$1,800 per window vs. $500-$900 non-impact." },
      { title: "Wind Zone 2 impact requirement", body: "Most other coastal counties require ASTM E1996 impact testing (or approved shutter protection). Adds $200-$500 per window." },
      { title: "Salt-air corrosion + UV", body: "Coastal Florida windows need marine-grade hardware and aluminum-clad or fiberglass frames. Vinyl frames degrade 30-40% faster than national average." },
    ],
    metros: [
      { city: "Miami-Dade (HVHZ)", low: 1050, high: 1850 },
      { city: "Fort Lauderdale (HVHZ)", low: 1020, high: 1780 },
      { city: "West Palm Beach (HVHZ)", low: 980, high: 1720 },
      { city: "Tampa Bay (Wind Zone 2)", low: 780, high: 1320 },
      { city: "St. Petersburg / Clearwater", low: 760, high: 1300 },
      { city: "Naples / Fort Myers (Wind Zone 3)", low: 880, high: 1520 },
      { city: "Sarasota / Bradenton", low: 800, high: 1360 },
      { city: "Orlando (inland)", low: 620, high: 1080 },
      { city: "Jacksonville", low: 640, high: 1100 },
      { city: "Tallahassee (inland)", low: 580, high: 1000 },
      { city: "Pensacola (Gulf Coast)", low: 720, high: 1240 },
    ],
  },
  GA: {
    headline: "Georgia window replacements run 4-9% below national — Atlanta market density keeps installer pricing competitive.",
    drivers: [
      { title: "Mixed-humid ENERGY STAR zone", body: "Standard Low-E double-pane meets Georgia ENERGY STAR spec. No premium triple-pane required." },
      { title: "Tree-canopy install challenges", body: "Atlanta's mature tree canopy means many homes need second-floor windows accessed from awkward angles — adds $50-$120 per window in install labor." },
      { title: "Coastal salt-air", body: "Savannah, Brunswick, and Tybee Island properties need marine-grade hardware." },
    ],
    metros: [
      { city: "Atlanta metro", low: 540, high: 920 },
      { city: "Savannah", low: 580, high: 980 },
      { city: "Augusta", low: 510, high: 870 },
      { city: "Athens", low: 520, high: 880 },
    ],
  },
  HI: {
    headline: "Hawaii window replacements run 35-55% above national — every window ships across the Pacific.",
    drivers: [
      { title: "Container shipping premium", body: "All windows ship from Mainland in containers. Freight + handling adds $100-$280 per window." },
      { title: "Trade-wind salt-air corrosion", body: "Windward coast properties need marine-grade aluminum or fiberglass frames + stainless hardware — vinyl is rarely viable beyond 5-7 years." },
      { title: "Solar-gain control + UV", body: "Year-round sun + Trade winds require premium Low-E coatings (SHGC ≤0.22) and UV-rated weatherstripping. Adds $60-$120 per window." },
    ],
    metros: [
      { city: "Honolulu / Oahu", low: 1080, high: 1850 },
      { city: "Maui", low: 1150, high: 1980 },
      { city: "Hilo / Big Island", low: 1180, high: 2020 },
      { city: "Kauai", low: 1220, high: 2080 },
    ],
  },
  ID: {
    headline: "Idaho window replacements run 5-10% below national — Boise + Coeur d'Alene growth keeping pricing reasonable.",
    drivers: [
      { title: "Cold-climate IECC zone", body: "Idaho Climate Zones 5-6 require U-factor ≤0.30, pushing argon-filled dual-pane or triple-pane spec at higher tiers." },
      { title: "Mountain-town premium", body: "Sun Valley, McCall, and Coeur d'Alene resort markets run 20-40% above Boise pricing." },
      { title: "Wildfire smoke filtration", body: "Some Idaho homeowners spec windows with enhanced weatherstripping to limit smoke infiltration — adds $40-$90 per window." },
    ],
    metros: [
      { city: "Boise", low: 580, high: 980 },
      { city: "Coeur d'Alene", low: 600, high: 1020 },
      { city: "Idaho Falls", low: 560, high: 940 },
      { city: "Sun Valley / Ketchum", low: 800, high: 1380 },
    ],
  },
  IL: {
    headline: "Illinois window replacements run 10-15% above national — Chicago labor market drives state-wide pricing.",
    drivers: [
      { title: "Cold-climate IECC + Chicago Energy Code", body: "Chicago Energy Conservation Code is stricter than state IECC — U-factor ≤0.28 and triple-pane common in new installs." },
      { title: "Historic-district approvals", body: "Many Chicago neighborhoods (Old Town, Lincoln Park, Wicker Park) have historic overlays requiring approved profiles and divided-lite patterns." },
      { title: "Lead-paint protocols", body: "Pre-1978 Chicago and suburban homes (huge stock) require RRP-certified installers — adds $80-$180 per window for lead-safe prep." },
    ],
    metros: [
      { city: "Chicago metro", low: 720, high: 1240 },
      { city: "Naperville / DuPage", low: 700, high: 1200 },
      { city: "Aurora", low: 660, high: 1140 },
      { city: "Joliet", low: 640, high: 1100 },
      { city: "Rockford", low: 620, high: 1080 },
      { city: "Bloomington-Normal", low: 600, high: 1040 },
      { city: "Champaign-Urbana", low: 610, high: 1060 },
      { city: "Peoria", low: 600, high: 1040 },
      { city: "Springfield", low: 590, high: 1020 },
      { city: "Decatur", low: 580, high: 1000 },
    ],
  },
  IN: {
    headline: "Indiana window replacements track close to national — Indianapolis at baseline with rural cost discount.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Indiana Climate Zones 4-5 require U-factor ≤0.32. Standard double-pane Low-E meets spec." },
      { title: "Manufacturing-belt labor pool", body: "Indiana has strong trade labor pool from manufacturing decline. Installer rates run $40-$60/hr." },
      { title: "Hardie + brick exteriors", body: "Many Indiana homes have full brick veneer exteriors — full-frame replacement requires brick mason coordination (~$200-$400 per opening)." },
    ],
    metros: [
      { city: "Indianapolis", low: 560, high: 960 },
      { city: "Fort Wayne", low: 520, high: 900 },
      { city: "Evansville", low: 510, high: 880 },
      { city: "South Bend", low: 540, high: 920 },
    ],
  },
  IA: {
    headline: "Iowa window replacements run 5-10% below national — Des Moines and Cedar Rapids hold prices competitive.",
    drivers: [
      { title: "Cold-climate IECC zone 6", body: "Iowa requires U-factor ≤0.30. Argon-filled dual-pane common; triple-pane in higher tiers." },
      { title: "Tornado-grade reinforcement", body: "Some Iowa insurers offer discounts for laminated-glass impact-rated windows ($90-$180 extra per window)." },
      { title: "Steady installer market", body: "Cedar Rapids, Des Moines, and Davenport installer pricing competitive — bid spread typically 15-25%." },
    ],
    metros: [
      { city: "Des Moines", low: 540, high: 920 },
      { city: "Cedar Rapids", low: 520, high: 900 },
      { city: "Davenport", low: 510, high: 880 },
      { city: "Iowa City", low: 530, high: 910 },
    ],
  },
  KS: {
    headline: "Kansas window replacements run 6-12% below national — tornado-belt reinforcement add-ons partially offset low base rates.",
    drivers: [
      { title: "Tornado-belt reinforcement", body: "Some Kansas insurers offer discounts (5-10% off premium) for impact-rated windows in tornado-prone zones." },
      { title: "Wichita and Kansas City Kansas labor pool", body: "Steady installer rates $38-$58/hr — below national average." },
      { title: "Hard-water etching on glass", body: "Eastern Kansas hard water can etch external glass with sprinkler overspray. Spec hydrophobic coatings if applicable." },
    ],
    metros: [
      { city: "Wichita", low: 500, high: 860 },
      { city: "Kansas City (KS)", low: 530, high: 910 },
      { city: "Topeka", low: 490, high: 840 },
      { city: "Lawrence", low: 510, high: 880 },
    ],
  },
  KY: {
    headline: "Kentucky window replacements run 8-13% below national — Louisville and Lexington hold competitive pricing.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Kentucky requires U-factor ≤0.32. Standard double-pane Low-E meets spec." },
      { title: "Historic stock", body: "Pre-1940 homes in Louisville (Old Louisville, Highlands) often have custom window sizes requiring more expensive made-to-measure replacements (~$100-$250 premium)." },
      { title: "Coal-region labor pool", body: "Eastern Kentucky benefits from displaced coal-industry labor entering trades — installer rates $34-$54/hr." },
    ],
    metros: [
      { city: "Louisville", low: 520, high: 900 },
      { city: "Lexington", low: 510, high: 880 },
      { city: "Bowling Green", low: 490, high: 850 },
      { city: "Northern KY (Covington)", low: 530, high: 910 },
    ],
  },
  LA: {
    headline: "Louisiana window replacements run 6-12% above national — coastal hurricane code adds cost.",
    drivers: [
      { title: "Hurricane impact rating (coastal)", body: "New Orleans + Baton Rouge + coastal parishes require ASTM E1996 impact-rated windows or approved shutter protection. Adds $200-$500 per window." },
      { title: "Humidity + termite damage", body: "Wood-frame windows in Louisiana have ~30% shorter lifespan due to humidity + termite pressure. Vinyl or fiberglass preferred." },
      { title: "Sales tax (9.5%)", body: "Louisiana state + local sales tax averages 9.5% on materials — among the highest in the U.S." },
    ],
    metros: [
      { city: "New Orleans (impact)", low: 720, high: 1240 },
      { city: "Baton Rouge", low: 660, high: 1140 },
      { city: "Shreveport", low: 580, high: 1000 },
      { city: "Lafayette", low: 620, high: 1080 },
    ],
  },
  ME: {
    headline: "Maine window replacements run 12-20% above national — Northeast labor + cold-climate spec.",
    drivers: [
      { title: "Climate Zone 6/7 triple-pane", body: "Northern Maine often requires triple-pane glass for code compliance. Adds $250-$450 per window over double-pane." },
      { title: "Historic district approvals", body: "Portland, Bangor, Bar Harbor have historic overlays requiring approved profiles." },
      { title: "Short install season", body: "Reliable install weather May-October. Peak labor rates June-August $70-$100/hr." },
    ],
    metros: [
      { city: "Portland", low: 680, high: 1180 },
      { city: "Bangor", low: 640, high: 1100 },
      { city: "Augusta", low: 620, high: 1080 },
      { city: "Bar Harbor (resort)", low: 800, high: 1380 },
    ],
  },
  MD: {
    headline: "Maryland window replacements run 12-18% above national — Baltimore metro + DC commute belt drive pricing.",
    drivers: [
      { title: "Mid-Atlantic IECC zone", body: "Maryland Climate Zones 4-5 require U-factor ≤0.30." },
      { title: "Lead-paint protocols", body: "Pre-1978 Baltimore rowhomes (massive stock) require RRP-certified installers — adds $80-$180 per window." },
      { title: "DC commute-belt labor premium", body: "Montgomery, Prince George's, and Howard County labor rates pulled up by DC market — $70-$105/hr." },
    ],
    metros: [
      { city: "Baltimore", low: 680, high: 1180 },
      { city: "Bethesda / Rockville", low: 760, high: 1320 },
      { city: "Annapolis", low: 720, high: 1240 },
      { city: "Frederick", low: 640, high: 1100 },
    ],
  },
  MA: {
    headline: "Massachusetts window replacements run 18-28% above national — Boston labor market is one of the highest in the U.S.",
    drivers: [
      { title: "MA Stretch Energy Code", body: "Many MA municipalities adopt the Stretch Code requiring U-factor ≤0.27 and HERS rating compliance. Triple-pane common." },
      { title: "Historic district approvals", body: "Beacon Hill, Back Bay, North End, Cambridge historic districts require approved window profiles — adds 2-8 weeks to permitting." },
      { title: "Boston-metro labor rates", body: "Installer rates $85-$130/hr in Boston metro." },
    ],
    metros: [
      { city: "Boston", low: 880, high: 1520 },
      { city: "Cambridge", low: 900, high: 1560 },
      { city: "Worcester", low: 720, high: 1240 },
      { city: "Springfield", low: 680, high: 1180 },
    ],
  },
  MI: {
    headline: "Michigan window replacements run 4-10% above national — cold-climate code, snowbelt installs, Detroit revival.",
    drivers: [
      { title: "Climate Zone 5/6 spec", body: "Michigan requires U-factor ≤0.30. Argon-filled dual-pane standard; triple-pane in Upper Peninsula." },
      { title: "Snowbelt freeze-thaw stress", body: "Western Michigan and UP get 60-100+ freeze-thaw cycles. Quality install with butyl-flange seal adds $40-$80 per window." },
      { title: "Detroit-area trade labor", body: "Detroit + suburbs have strong unionized trade labor — pricing reasonable but lead times can stretch 4-8 weeks in peak season." },
    ],
    metros: [
      { city: "Detroit metro", low: 600, high: 1040 },
      { city: "Grand Rapids", low: 580, high: 1000 },
      { city: "Lansing", low: 570, high: 980 },
      { city: "Ann Arbor", low: 640, high: 1100 },
      { city: "Traverse City (resort)", low: 700, high: 1200 },
    ],
  },
  MN: {
    headline: "Minnesota window replacements run 8-14% above national — Climate Zone 7 cold spec drives triple-pane prevalence.",
    drivers: [
      { title: "Triple-pane common spec", body: "Minnesota Climate Zone 7 requires U-factor ≤0.27. Most quality installs use triple-pane argon-filled glass — adds $250-$450 per window over double-pane." },
      { title: "Twin Cities labor market", body: "Minneapolis-St. Paul installer rates $65-$95/hr. Bid spread tight due to deep contractor pool." },
      { title: "Frost-heave foundation movement", body: "Minnesota's deep frost line (60-72\") causes seasonal foundation movement that stresses window flashing. Quality installs use double-flange flashing — adds $30-$60 per window." },
    ],
    metros: [
      { city: "Minneapolis-St. Paul", low: 680, high: 1180 },
      { city: "Duluth", low: 660, high: 1140 },
      { city: "Rochester", low: 640, high: 1100 },
      { city: "St. Cloud", low: 620, high: 1080 },
    ],
  },
  MS: {
    headline: "Mississippi window replacements run 14-20% below national — among the cheapest states for window replacement.",
    drivers: [
      { title: "Southern climate zone ENERGY STAR", body: "Standard Low-E double-pane meets Mississippi spec easily." },
      { title: "Hurricane impact (Gulf Coast)", body: "Hancock, Harrison, Jackson counties require impact-rated windows — adds $200-$500 per window." },
      { title: "Lowest installer rates", body: "Mississippi trade labor rates run $28-$48/hr — among the lowest in the U.S." },
    ],
    metros: [
      { city: "Jackson", low: 460, high: 800 },
      { city: "Gulfport (impact)", low: 640, high: 1100 },
      { city: "Hattiesburg", low: 440, high: 770 },
      { city: "Tupelo", low: 430, high: 760 },
    ],
  },
  MO: {
    headline: "Missouri window replacements run 5-10% below national — Kansas City and St. Louis hold competitive pricing.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Missouri Climate Zones 4-5 require U-factor ≤0.32." },
      { title: "Tornado-grade options", body: "Some Missouri insurers offer discounts for impact-rated windows in high-tornado zones." },
      { title: "Steady installer market", body: "Kansas City + St. Louis have strong installer competition." },
    ],
    metros: [
      { city: "Kansas City (MO)", low: 540, high: 920 },
      { city: "St. Louis", low: 560, high: 960 },
      { city: "Springfield", low: 500, high: 860 },
      { city: "Columbia", low: 520, high: 890 },
    ],
  },
  MT: {
    headline: "Montana window replacements run 5-12% above national — large state, thin installer pool outside Billings/Missoula.",
    drivers: [
      { title: "Climate Zone 6 spec", body: "Montana requires U-factor ≤0.30. Triple-pane common in higher elevations." },
      { title: "Drive-time premiums", body: "Outside major metros, drive time can add 15-30% to small jobs." },
      { title: "Wildfire smoke filtration", body: "Some homes spec enhanced weatherstripping to limit summer smoke infiltration." },
    ],
    metros: [
      { city: "Billings", low: 580, high: 1000 },
      { city: "Missoula", low: 600, high: 1040 },
      { city: "Bozeman", low: 640, high: 1100 },
      { city: "Great Falls", low: 560, high: 960 },
    ],
  },
  NE: {
    headline: "Nebraska window replacements run 6-12% below national — Omaha and Lincoln hold competitive installer pricing.",
    drivers: [
      { title: "Cold-climate spec", body: "Nebraska Climate Zone 5 requires U-factor ≤0.30. Argon-filled dual-pane meets spec." },
      { title: "Tornado-grade reinforcement", body: "Some insurers offer discounts for impact-rated windows." },
      { title: "Steady labor market", body: "Omaha and Lincoln have stable installer competition." },
    ],
    metros: [
      { city: "Omaha", low: 530, high: 920 },
      { city: "Lincoln", low: 510, high: 890 },
      { city: "Grand Island", low: 480, high: 840 },
    ],
  },
  NV: {
    headline: "Nevada window replacements run 4-9% above national — Las Vegas heat + Reno labor pull up pricing.",
    drivers: [
      { title: "UV-heavy Low-E requirement", body: "Las Vegas climate demands SHGC ≤0.25. Premium Low-E coatings (Cardinal LoE-366) add $40-$80 per window but cut summer cooling 20-30%." },
      { title: "Stucco full-frame install", body: "Most Vegas-metro homes have stucco exteriors; full-frame replacement requires stucco cut/patch (+$200-$400 per opening)." },
      { title: "Reno casino-driven labor", body: "Reno installer rates $55-$85/hr — higher than Las Vegas due to casino construction competition." },
    ],
    metros: [
      { city: "Las Vegas metro", low: 620, high: 1080 },
      { city: "Reno", low: 660, high: 1140 },
      { city: "Henderson", low: 630, high: 1100 },
      { city: "Carson City", low: 600, high: 1040 },
    ],
  },
  NH: {
    headline: "New Hampshire window replacements run 12-20% above national — Northeast cold-climate spec + premium clientele.",
    drivers: [
      { title: "Triple-pane prevalence", body: "Climate Zone 6 commonly drives triple-pane glass spec, especially in the Lakes Region and White Mountains." },
      { title: "Historic district approvals", body: "Portsmouth, Concord, and many town centers have historic overlays." },
      { title: "Premium-clientele markets", body: "Lakes Region (Wolfeboro, Meredith) and Seacoast (Rye, Portsmouth) command 20-35% premium over Manchester baseline." },
    ],
    metros: [
      { city: "Manchester", low: 680, high: 1180 },
      { city: "Nashua", low: 700, high: 1200 },
      { city: "Portsmouth", low: 780, high: 1340 },
      { city: "Concord", low: 660, high: 1140 },
    ],
  },
  NJ: {
    headline: "New Jersey window replacements run 15-25% above national — Northeast labor + coastal impact code.",
    drivers: [
      { title: "Coastal impact rating", body: "Atlantic, Cape May, Ocean, Monmouth counties require ASTM E1996 impact-rated windows in many beach communities." },
      { title: "Tri-state labor market", body: "Bergen, Hudson, Essex installer rates pulled up by NYC market — $80-$120/hr typical." },
      { title: "Historic district approvals", body: "Cape May, Princeton, Hoboken historic overlays add 2-6 weeks to permitting." },
    ],
    metros: [
      { city: "Newark / Northern NJ", low: 800, high: 1380 },
      { city: "Jersey City / Hudson", low: 820, high: 1420 },
      { city: "Princeton", low: 760, high: 1320 },
      { city: "Atlantic City (impact)", low: 880, high: 1520 },
    ],
  },
  NM: {
    headline: "New Mexico window replacements run 0-5% below national — Albuquerque density holds prices reasonable.",
    drivers: [
      { title: "High-altitude UV", body: "Albuquerque (5,000 ft) and Santa Fe (7,200 ft) UV intensity is 25-35% above sea level. Premium Low-E coatings strongly recommended." },
      { title: "Adobe and stucco wall systems", body: "60%+ of NM homes have adobe or stucco — full-frame replacement requires specialty patching ($200-$400 per opening)." },
      { title: "Thin installer pool outside Albuquerque", body: "Las Cruces, Farmington, Roswell see drive-time premiums of 15-30%." },
    ],
    metros: [
      { city: "Albuquerque", low: 540, high: 940 },
      { city: "Santa Fe", low: 620, high: 1080 },
      { city: "Las Cruces", low: 520, high: 900 },
      { city: "Farmington", low: 540, high: 940 },
    ],
  },
  NY: {
    headline: "New York window replacements run 25-40% above national — NYC labor market is the most expensive in the U.S. for window installs.",
    drivers: [
      { title: "NYC labor + access premium", body: "Manhattan, Brooklyn, Queens installer rates $110-$170/hr. Co-op approvals + scaffold permits + delivery logistics add $300-$800 per window over standard." },
      { title: "Stretch Energy Code (NYC + Westchester)", body: "NYC Energy Conservation Code requires U-factor ≤0.27. Triple-pane very common." },
      { title: "Historic district approvals", body: "Brownstone districts (Brooklyn Heights, Park Slope, Cobble Hill, UWS, UES) require approved profiles — adds 4-8 weeks to permitting." },
    ],
    metros: [
      { city: "Manhattan", low: 1280, high: 2280 },
      { city: "Brooklyn / Queens", low: 1080, high: 1880 },
      { city: "Bronx", low: 1020, high: 1780 },
      { city: "Staten Island", low: 940, high: 1640 },
      { city: "Long Island (Nassau, Suffolk)", low: 920, high: 1620 },
      { city: "Westchester", low: 980, high: 1720 },
      { city: "Albany / Capital Region", low: 700, high: 1220 },
      { city: "Buffalo", low: 680, high: 1180 },
      { city: "Rochester", low: 670, high: 1160 },
      { city: "Syracuse", low: 660, high: 1140 },
      { city: "Yonkers", low: 940, high: 1640 },
    ],
  },
  NC: {
    headline: "North Carolina window replacements track close to national — Research Triangle and Charlotte hold pricing competitive.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Standard double-pane Low-E meets NC spec." },
      { title: "Coastal impact rating", body: "Outer Banks + coastal counties require ASTM E1996 impact-rated windows." },
      { title: "Tree canopy install premium", body: "Tall pine + oak canopies in older Raleigh/Durham/Charlotte neighborhoods make second-floor access difficult." },
    ],
    metros: [
      { city: "Raleigh-Durham", low: 580, high: 1000 },
      { city: "Charlotte", low: 600, high: 1040 },
      { city: "Wilmington (coastal impact)", low: 720, high: 1240 },
      { city: "Asheville", low: 620, high: 1080 },
    ],
  },
  ND: {
    headline: "North Dakota window replacements run 10-15% above national — Climate Zone 7 triple-pane spec + thin installer pool.",
    drivers: [
      { title: "Triple-pane required", body: "North Dakota Climate Zone 7 requires U-factor ≤0.27 — triple-pane glass is standard, not premium." },
      { title: "Bakken-area labor pull", body: "Oil-field labor competition raises installer rates in Williston/Dickinson — $70-$100/hr." },
      { title: "Short install season", body: "Reliable weather May-September; July is peak demand." },
    ],
    metros: [
      { city: "Fargo", low: 620, high: 1080 },
      { city: "Bismarck", low: 600, high: 1040 },
      { city: "Grand Forks", low: 580, high: 1000 },
      { city: "Williston (oil region)", low: 720, high: 1240 },
    ],
  },
  OH: {
    headline: "Ohio window replacements run 2-7% below national — Cleveland, Columbus, Cincinnati all have strong installer competition.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Ohio Climate Zones 4-5 require U-factor ≤0.32." },
      { title: "Lead-paint protocols", body: "Pre-1978 housing in Cleveland, Cincinnati, Toledo requires RRP-certified installers." },
      { title: "Manufacturing-belt labor", body: "Strong trade-labor pool from manufacturing — installer rates $42-$62/hr." },
    ],
    metros: [
      { city: "Columbus", low: 560, high: 960 },
      { city: "Cleveland", low: 580, high: 1000 },
      { city: "Cincinnati", low: 570, high: 980 },
      { city: "Akron", low: 540, high: 920 },
      { city: "Toledo", low: 530, high: 910 },
      { city: "Dayton", low: 540, high: 930 },
      { city: "Youngstown", low: 510, high: 880 },
      { city: "Canton", low: 520, high: 890 },
      { city: "Lorain / Elyria", low: 540, high: 920 },
      { city: "Mansfield", low: 510, high: 870 },
    ],
  },
  OK: {
    headline: "Oklahoma window replacements run 8-14% below national — strong installer competition + tornado-rated upgrade options.",
    drivers: [
      { title: "Tornado-belt impact options", body: "Many OK insurers offer 5-10% premium discounts for impact-rated laminated-glass windows." },
      { title: "Steady installer pool", body: "Oklahoma City and Tulsa have stable installer competition." },
      { title: "Low installer rates", body: "Trade labor $35-$55/hr." },
    ],
    metros: [
      { city: "Oklahoma City", low: 510, high: 880 },
      { city: "Tulsa", low: 500, high: 870 },
      { city: "Norman", low: 510, high: 880 },
      { city: "Lawton", low: 480, high: 830 },
    ],
  },
  OR: {
    headline: "Oregon window replacements run 14-22% above national — Portland labor + Pacific Northwest energy code.",
    drivers: [
      { title: "OR Reach Code adoption", body: "Many Portland-metro jurisdictions adopt the Reach Code requiring U-factor ≤0.28 and ENERGY STAR Most Efficient ratings." },
      { title: "Lead-paint protocols", body: "Pre-1978 Portland and Salem homes (huge stock) require RRP-certified installers." },
      { title: "Pacific NW labor", body: "Portland installer rates $70-$110/hr. Compressed June-September install season pushes peak pricing." },
    ],
    metros: [
      { city: "Portland metro", low: 720, high: 1240 },
      { city: "Salem", low: 660, high: 1140 },
      { city: "Eugene", low: 680, high: 1180 },
      { city: "Bend", low: 720, high: 1240 },
    ],
  },
  PA: {
    headline: "Pennsylvania window replacements run 6-12% above national — Philadelphia and Pittsburgh markets are stable with strong competition.",
    drivers: [
      { title: "Mid-Atlantic IECC zone", body: "Pennsylvania Climate Zones 5-6 require U-factor ≤0.30." },
      { title: "Lead-paint protocols", body: "Pre-1978 Philadelphia rowhomes + Pittsburgh neighborhoods require RRP-certified installers." },
      { title: "Historic district approvals", body: "Society Hill, Old City, Mt. Washington, and many historic downtowns require approved profiles." },
    ],
    metros: [
      { city: "Philadelphia", low: 660, high: 1140 },
      { city: "Pittsburgh", low: 620, high: 1080 },
      { city: "Harrisburg", low: 580, high: 1000 },
      { city: "Allentown / Lehigh Valley", low: 640, high: 1100 },
    ],
  },
  RI: {
    headline: "Rhode Island window replacements run 14-22% above national — Newport historic and Providence urban markets.",
    drivers: [
      { title: "Northeast cold-climate spec", body: "RI Climate Zone 5 requires U-factor ≤0.30. Argon-filled dual-pane standard." },
      { title: "Historic district approvals", body: "Newport, Bristol, and Providence historic overlays require approved profiles." },
      { title: "Coastal salt-air", body: "Newport, Narragansett, and Block Island properties need marine-grade hardware." },
    ],
    metros: [
      { city: "Providence", low: 700, high: 1220 },
      { city: "Newport", low: 780, high: 1340 },
      { city: "Warwick", low: 680, high: 1180 },
    ],
  },
  SC: {
    headline: "South Carolina window replacements run 2-8% below national — Charleston + Greenville hold competitive pricing.",
    drivers: [
      { title: "Coastal impact rating", body: "Charleston, Beaufort, Hilton Head + Horry County require impact-rated windows." },
      { title: "Historic district approvals", body: "Charleston Historic District is one of the strictest in the U.S. — approved profiles + divided lites required, often custom-ordered." },
      { title: "Growth-driven labor market", body: "Greenville-Spartanburg booming — installer rates rising to $50-$75/hr." },
    ],
    metros: [
      { city: "Charleston (historic)", low: 720, high: 1280 },
      { city: "Greenville", low: 560, high: 960 },
      { city: "Columbia", low: 540, high: 920 },
      { city: "Myrtle Beach (impact)", low: 700, high: 1200 },
    ],
  },
  SD: {
    headline: "South Dakota window replacements run 5-10% below national — small installer market with steady pricing.",
    drivers: [
      { title: "Climate Zone 6 spec", body: "South Dakota requires U-factor ≤0.30. Argon-filled dual-pane meets spec; triple-pane in higher elevations." },
      { title: "Thin installer pool", body: "Outside Sioux Falls and Rapid City, drive-time premiums of 15-25%." },
      { title: "Tornado-belt reinforcement", body: "Some insurers offer impact-window discounts." },
    ],
    metros: [
      { city: "Sioux Falls", low: 540, high: 920 },
      { city: "Rapid City", low: 520, high: 900 },
      { city: "Aberdeen", low: 500, high: 870 },
    ],
  },
  TN: {
    headline: "Tennessee window replacements run 4-9% below national — Nashville, Memphis, Knoxville all have solid installer competition.",
    drivers: [
      { title: "Mixed-humid IECC zone", body: "Tennessee Climate Zones 3-4 require U-factor ≤0.35 (mild) to ≤0.32. Standard double-pane Low-E meets spec." },
      { title: "Nashville growth premium", body: "Nashville installer rates rising 8-15% above the rest of the state due to construction boom." },
      { title: "Tornado-belt options", body: "Mid- and West Tennessee insurers sometimes offer impact-window discounts." },
    ],
    metros: [
      { city: "Nashville", low: 580, high: 1000 },
      { city: "Memphis", low: 520, high: 900 },
      { city: "Knoxville", low: 540, high: 920 },
      { city: "Chattanooga", low: 530, high: 910 },
    ],
  },
  TX: {
    headline: "Texas window replacements run 2-8% below national — deep installer pools across 4 major metros + no state income tax.",
    drivers: [
      { title: "Hot-climate Low-E requirement", body: "Texas climate zones 2-3 require SHGC ≤0.25 in most counties. Premium Low-E coatings cut cooling costs significantly." },
      { title: "Coastal impact rating", body: "Galveston, Brazoria, Matagorda, Cameron counties require impact-rated windows." },
      { title: "Deep contractor pool", body: "DFW, Houston, Austin, San Antonio all have 100+ active window installers. Tight bid spread." },
    ],
    metros: [
      { city: "Dallas-Fort Worth", low: 520, high: 900 },
      { city: "Houston (coastal impact)", low: 620, high: 1080 },
      { city: "Austin", low: 580, high: 1000 },
      { city: "San Antonio", low: 540, high: 920 },
      { city: "El Paso", low: 500, high: 860 },
      { city: "Corpus Christi (coastal impact)", low: 600, high: 1040 },
      { city: "McAllen / Rio Grande Valley", low: 480, high: 820 },
      { city: "Lubbock", low: 490, high: 840 },
      { city: "Waco", low: 510, high: 880 },
      { city: "Amarillo", low: 480, high: 820 },
    ],
  },
  UT: {
    headline: "Utah window replacements run 2-7% above national — Salt Lake growth + Park City premium.",
    drivers: [
      { title: "Cold-climate IECC zone", body: "Utah Climate Zone 5 requires U-factor ≤0.30. Triple-pane common at higher elevations." },
      { title: "Inversion + UV", body: "Salt Lake Valley inversion-layer winter pollution + 4,200 ft UV demand premium Low-E + good weatherstripping." },
      { title: "Park City / resort premium", body: "Park City, Deer Valley, Sundance resort areas run 30-50% above SLC baseline." },
    ],
    metros: [
      { city: "Salt Lake City", low: 580, high: 1000 },
      { city: "Provo", low: 560, high: 960 },
      { city: "Ogden", low: 540, high: 920 },
      { city: "Park City (resort)", low: 820, high: 1420 },
    ],
  },
  VT: {
    headline: "Vermont window replacements run 14-22% above national — Climate Zone 6 spec + small contractor pool.",
    drivers: [
      { title: "Climate Zone 6 triple-pane prevalence", body: "Vermont requires U-factor ≤0.30; triple-pane common, especially in higher-elevation Green Mountain communities." },
      { title: "Small installer pool", body: "Burlington has solid installer market; rural Vermont can see 20-35% drive-time premiums." },
      { title: "Historic district approvals", body: "Burlington, Montpelier, and many town centers have historic overlays." },
    ],
    metros: [
      { city: "Burlington", low: 700, high: 1200 },
      { city: "Montpelier", low: 680, high: 1180 },
      { city: "Stowe (resort)", low: 840, high: 1440 },
    ],
  },
  VA: {
    headline: "Virginia window replacements run 6-12% above national — DC commute-belt labor + coastal impact zones.",
    drivers: [
      { title: "NoVA labor premium", body: "Fairfax, Arlington, Loudoun installer rates pulled up by DC market — $80-$120/hr." },
      { title: "Coastal impact rating", body: "Virginia Beach, Norfolk, Eastern Shore require impact-rated windows in coastal zones." },
      { title: "Historic district approvals", body: "Old Town Alexandria, Williamsburg, Charlottesville historic overlays require approved profiles." },
    ],
    metros: [
      { city: "Northern VA (Arlington)", low: 780, high: 1340 },
      { city: "Richmond", low: 620, high: 1080 },
      { city: "Virginia Beach (coastal)", low: 680, high: 1180 },
      { city: "Charlottesville", low: 640, high: 1100 },
    ],
  },
  WA: {
    headline: "Washington window replacements run 18-28% above national — Seattle labor + sales tax on labor + PNW energy code.",
    drivers: [
      { title: "WA Energy Code", body: "Washington adopts the State Energy Code requiring U-factor ≤0.28. Triple-pane increasingly common." },
      { title: "Sales tax on labor", body: "Washington's 8.7-10.5% sales tax applies to BOTH materials AND labor on home improvement — unique among major states." },
      { title: "Tech-driven labor competition", body: "Seattle metro installer rates $80-$130/hr." },
    ],
    metros: [
      { city: "Seattle metro", low: 880, high: 1520 },
      { city: "Bellevue / Eastside", low: 920, high: 1580 },
      { city: "Tacoma", low: 760, high: 1320 },
      { city: "Spokane", low: 640, high: 1100 },
    ],
  },
  WV: {
    headline: "West Virginia window replacements run 8-14% below national — old housing stock and hilly-terrain install challenges offset cheap labor.",
    drivers: [
      { title: "Old housing stock + lead paint", body: "60%+ of WV homes are pre-1978 — RRP-certified installers required. Adds $80-$180 per window for lead-safe prep." },
      { title: "Hilly-terrain access", body: "Many WV homes sit on slopes requiring boom-lift or rope access for upper-floor windows. Adds $80-$200 per window." },
      { title: "Thin installer pool outside major metros", body: "Charleston, Morgantown, Huntington have steady installers; rural areas see 20-35% drive-time premiums." },
    ],
    metros: [
      { city: "Charleston (WV)", low: 510, high: 890 },
      { city: "Morgantown", low: 530, high: 920 },
      { city: "Huntington", low: 500, high: 870 },
    ],
  },
  WI: {
    headline: "Wisconsin window replacements run 4-10% above national — cold-climate spec + Milwaukee/Madison labor.",
    drivers: [
      { title: "Climate Zone 6/7 triple-pane prevalence", body: "Wisconsin requires U-factor ≤0.30; northern WI commonly triple-pane." },
      { title: "Lead-paint protocols", body: "Pre-1978 Milwaukee and Madison housing stock requires RRP-certified installers." },
      { title: "Snowbelt freeze-thaw stress", body: "60-90 freeze-thaw cycles per year — quality install with butyl-flange flashing recommended." },
    ],
    metros: [
      { city: "Milwaukee", low: 620, high: 1080 },
      { city: "Madison", low: 600, high: 1040 },
      { city: "Green Bay", low: 580, high: 1000 },
      { city: "Eau Claire", low: 560, high: 960 },
    ],
  },
  WY: {
    headline: "Wyoming window replacements run 5-12% above national — thin installer market plus Climate Zone 6/7 spec.",
    drivers: [
      { title: "Triple-pane prevalence", body: "Most of Wyoming Climate Zone 6-7 makes triple-pane standard for code compliance." },
      { title: "Wind-load reinforcement", body: "Wyoming has the highest sustained wind speeds in the U.S. Many homes require reinforced window framing — adds $50-$120 per window." },
      { title: "Thin installer market", body: "Cheyenne and Casper have stable installers; smaller towns see 25-40% drive-time premiums." },
    ],
    metros: [
      { city: "Cheyenne", low: 580, high: 1000 },
      { city: "Casper", low: 600, high: 1040 },
      { city: "Jackson (resort)", low: 880, high: 1520 },
    ],
  },
  DC: {
    headline: "Washington DC window replacements run 22-32% above national — historic district density + federal-region labor + co-op approvals.",
    drivers: [
      { title: "Historic district approvals", body: "Capitol Hill, Georgetown, Dupont Circle, Logan Circle, U Street all have historic overlays requiring approved profiles and divided lites." },
      { title: "Federal-region labor", body: "DC installer rates $85-$130/hr, pulled up by federal contracting market." },
      { title: "Rowhome access constraints", body: "Many DC rowhomes have narrow alley access requiring smaller crews + extended install time." },
    ],
    metros: [
      { city: "Washington DC", low: 820, high: 1480 },
    ],
  },
};

/** Get window-replacement-specific drivers + metro pricing for a state. */
export function getWindowDrivers(stateCode) {
  return WINDOW_DRIVERS[stateCode] || null;
}
